import React, { useEffect, useState, createContext, useContext, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, ArrowLeftIcon, DatabaseIcon, ZapIcon, CheckCircle2Icon, BrainCircuitIcon, UploadCloudIcon, FileTextIcon, SparklesIcon, CalendarIcon, UsersIcon, ClipboardCheckIcon, PaletteIcon, SendIcon, QrCodeIcon, MessageSquareIcon, ImageIcon, BarChart3Icon, ShieldCheckIcon, CrownIcon, Mic2Icon, HandshakeIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
// Shared tutorial visibility context
const TutorialContext = createContext<boolean>(true);
/* -----------------------------------------------------------
   Tutorial bubble system
----------------------------------------------------------- */
type BubblePos = {
  top: string;
  left?: string;
  right?: string;
} | {
  bottom: string;
  left?: string;
  right?: string;
};
type Bubble = {
  n: number | string;
  text: string;
  pos: BubblePos;
  arrow: 'top' | 'bottom' | 'left' | 'right';
  color?: 'coral' | 'teal' | 'navy' | 'amber' | 'purple' | 'pink';
};
function TutorialBubble({
  b
}: {
  b: Bubble;
}) {
  const showTutorials = useContext(TutorialContext);
  const [dismissed, setDismissed] = useState(false);
  if (!showTutorials || dismissed) return null;
  const colorMap = {
    coral: 'bg-brand-coral border-brand-coral text-white',
    teal: 'bg-brand-teal border-brand-teal text-white',
    navy: 'bg-brand-navy border-brand-navy text-white',
    amber: 'bg-amber-500 border-amber-500 text-white',
    purple: 'bg-purple-600 border-purple-600 text-white',
    pink: 'bg-pink-500 border-pink-500 text-white'
  };
  const arrowMap = {
    top: 'after:absolute after:bottom-full after:left-6 after:border-8 after:border-transparent',
    bottom: 'after:absolute after:top-full after:left-6 after:border-8 after:border-transparent',
    left: 'after:absolute after:right-full after:top-4 after:border-8 after:border-transparent',
    right: 'after:absolute after:left-full after:top-4 after:border-8 after:border-transparent'
  };
  const color = b.color || 'coral';
  const arrowBorderColor = {
    coral: {
      top: 'after:border-b-brand-coral',
      bottom: 'after:border-t-brand-coral',
      left: 'after:border-r-brand-coral',
      right: 'after:border-l-brand-coral'
    },
    teal: {
      top: 'after:border-b-brand-teal',
      bottom: 'after:border-t-brand-teal',
      left: 'after:border-r-brand-teal',
      right: 'after:border-l-brand-teal'
    },
    navy: {
      top: 'after:border-b-brand-navy',
      bottom: 'after:border-t-brand-navy',
      left: 'after:border-r-brand-navy',
      right: 'after:border-l-brand-navy'
    },
    amber: {
      top: 'after:border-b-amber-500',
      bottom: 'after:border-t-amber-500',
      left: 'after:border-r-amber-500',
      right: 'after:border-l-amber-500'
    },
    purple: {
      top: 'after:border-b-purple-600',
      bottom: 'after:border-t-purple-600',
      left: 'after:border-r-purple-600',
      right: 'after:border-l-purple-600'
    },
    pink: {
      top: 'after:border-b-pink-500',
      bottom: 'after:border-t-pink-500',
      left: 'after:border-r-pink-500',
      right: 'after:border-l-pink-500'
    }
  };
  return <motion.div initial={{
    opacity: 0,
    scale: 0.9,
    y: 4
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} transition={{
    duration: 0.25,
    ease: [0.22, 1, 0.36, 1]
  }} className={`absolute z-30 max-w-[240px] ${colorMap[color]} rounded-xl p-3 shadow-2xl border-2 ${arrowMap[b.arrow]} ${arrowBorderColor[color][b.arrow]} group`} style={b.pos as any} data-id="element-5751">
      <div className="flex items-start gap-2" data-id="element-5752">
        <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-xs font-black shrink-0" data-id="element-5753">
          {b.n}
        </div>
        <p className="text-xs leading-snug font-medium pr-3" data-id="element-5754">{b.text}</p>
        <button type="button" aria-label="Dismiss tutorial bubble" onClick={() => setDismissed(true)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white text-slate-700 border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-100 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100" data-id="element-5755">
          <XIcon className="w-3 h-3" data-id="element-5756" />
        </button>
      </div>
    </motion.div>;
}
/* -----------------------------------------------------------
   Browser chrome wrapper for UI mocks
----------------------------------------------------------- */
function BrowserChrome({
  children,
  url,
  role
}: {
  children: React.ReactNode;
  url: string;
  role: string;
}) {
  return <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden" data-id="element-5757">
      <div className="bg-slate-100 border-b border-slate-200 px-4 py-2.5 flex items-center gap-2" data-id="element-5758">
        <div className="flex gap-1.5" data-id="element-5759">
          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-5760" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-5761" />
          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-5762" />
        </div>
        <div className="flex-1 bg-white rounded-md px-3 py-1 ml-2 border border-slate-200 flex items-center gap-2" data-id="element-5763">
          <div className="w-2 h-2 rounded-full bg-brand-teal" data-id="element-5764" />
          <code className="text-[11px] text-slate-600 truncate" data-id="element-5765">{url}</code>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-2 py-0.5 rounded bg-slate-200" data-id="element-5766">
          {role}
        </div>
      </div>
      <div className="relative bg-slate-50 min-h-[480px]" data-id="element-5767">{children}</div>
    </div>;
}
/* -----------------------------------------------------------
   Footer panel: DB impact + What happens next
----------------------------------------------------------- */
function FooterPanel({
  db,
  next,
  ai
}: {
  db: {
    tables: string[];
    ops: string[];
  };
  next: string;
  ai?: AISkill;
}) {
  return <div className={`grid gap-3 mt-4 ${ai ? 'md:grid-cols-3' : 'md:grid-cols-2'}`} data-id="element-5768">
      {/* Database Impact */}
      <div className="bg-slate-900 text-white rounded-xl p-4 relative overflow-hidden" data-id="element-5769">
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-teal/10 rounded-full blur-2xl" data-id="element-5770" />
        <div className="relative" data-id="element-5771">
          <div className="flex items-center gap-2 mb-3" data-id="element-5772">
            <DatabaseIcon className="w-4 h-4 text-brand-teal" data-id="element-5773" />
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-teal" data-id="element-5774">
              Database Impact
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-5775">
            {db.tables.map((t, i) => <code key={i} className="text-[10px] font-mono bg-white/10 border border-white/20 px-2 py-1 rounded text-brand-teal" data-id="element-5776">
                {t}
              </code>)}
          </div>
          <ul className="space-y-1" data-id="element-5777">
            {db.ops.map((op, i) => <li key={i} className="text-[11px] text-slate-300 leading-snug flex items-start gap-1.5" data-id="element-5778">
                <span className="text-brand-teal mt-0.5" data-id="element-5779">›</span>
                <span data-id="element-5780">{op}</span>
              </li>)}
          </ul>
        </div>
      </div>

      {/* AI Skill (optional) */}
      {ai && <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-xl p-4 relative overflow-hidden" data-id="element-5781">
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-pink-300/20 rounded-full blur-2xl" data-id="element-5782" />
          <div className="relative" data-id="element-5783">
            <div className="flex items-center gap-2 mb-2" data-id="element-5784">
              <BrainCircuitIcon className="w-4 h-4 text-pink-200" data-id="element-5785" />
              <div className="text-[10px] font-bold uppercase tracking-wider text-pink-100" data-id="element-5786">
                Claude AI Skill
              </div>
            </div>
            <div className="text-sm font-bold leading-tight mb-1" data-id="element-5787">
              {ai.name}
            </div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono bg-white/10 border border-white/20 px-2 py-0.5 rounded text-pink-100 mb-2" data-id="element-5788">
              {ai.model}
            </div>
            <p className="text-[11px] text-purple-100 leading-snug mb-3" data-id="element-5789">
              {ai.purpose}
            </p>
            <div className="text-[9px] font-bold uppercase tracking-wider text-pink-200 mb-1" data-id="element-5790">
              Inputs
            </div>
            <ul className="space-y-0.5 mb-2" data-id="element-5791">
              {ai.inputs.map((inp, i) => <li key={i} className="text-[11px] text-purple-100 leading-snug flex items-start gap-1.5" data-id="element-5792">
                  <span className="text-pink-200 mt-0.5" data-id="element-5793">›</span>
                  <span data-id="element-5794">{inp}</span>
                </li>)}
            </ul>
            <div className="text-[9px] font-bold uppercase tracking-wider text-pink-200 mb-1" data-id="element-5795">
              Outputs
            </div>
            <ul className="space-y-0.5" data-id="element-5796">
              {ai.outputs.map((out, i) => <li key={i} className="text-[11px] text-purple-100 leading-snug flex items-start gap-1.5" data-id="element-5797">
                  <SparklesIcon className="w-2.5 h-2.5 text-pink-200 shrink-0 mt-0.5" data-id="element-5798" />
                  <span data-id="element-5799">{out}</span>
                </li>)}
            </ul>
            {ai.tone && <div className="mt-3 pt-3 border-t border-white/10 text-[10px] text-pink-200" data-id="element-5800">
                <span className="font-bold uppercase tracking-wider" data-id="element-5801">
                  Guardrail ·{' '}
                </span>
                <span className="text-purple-100" data-id="element-5802">{ai.tone}</span>
              </div>}
          </div>
        </div>}

      {/* What happens next */}
      <div className="bg-gradient-to-br from-brand-coral to-brand-coral/85 text-white rounded-xl p-4 relative overflow-hidden" data-id="element-5803">
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-yellow-200/10 rounded-full blur-2xl" data-id="element-5804" />
        <div className="relative" data-id="element-5805">
          <div className="flex items-center gap-2 mb-3" data-id="element-5806">
            <ZapIcon className="w-4 h-4 text-yellow-200" data-id="element-5807" />
            <div className="text-[10px] font-bold uppercase tracking-wider text-yellow-100" data-id="element-5808">
              What happens next
            </div>
          </div>
          <p className="text-sm leading-relaxed font-medium" data-id="element-5809">{next}</p>
        </div>
      </div>
    </div>;
}
/* -----------------------------------------------------------
   Screen primitives
----------------------------------------------------------- */
function Card({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`bg-white rounded-xl border border-slate-200 ${className}`} data-id="element-5810">
      {children}
    </div>;
}
function Pill({
  children,
  color = 'slate'
}: {
  children: React.ReactNode;
  color?: 'slate' | 'coral' | 'teal' | 'amber' | 'purple' | 'green';
}) {
  const colors = {
    slate: 'bg-slate-100 text-slate-700',
    coral: 'bg-brand-coral/10 text-brand-coral',
    teal: 'bg-brand-teal/10 text-brand-teal',
    amber: 'bg-amber-100 text-amber-700',
    purple: 'bg-purple-100 text-purple-700',
    green: 'bg-green-100 text-green-700'
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${colors[color]}`} data-id="element-5811">
      {children}
    </span>;
}
/* ===========================================================
   ROLE SCREEN COMPONENTS
=========================================================== */
/* ----- P1 · Hotel Sales Manager ----- */
function P1Screen1() {
  return <BrowserChrome role="Sales Manager" url="offsiteplus.app/sales/upload" data-id="element-5812">
      <div className="p-6 relative" data-id="element-5813">
        <div className="flex items-center justify-between mb-5" data-id="element-5814">
          <div data-id="element-5815">
            <h3 className="text-lg font-bold text-brand-navy" data-id="element-5816">
              Upload your book of business
            </h3>
            <p className="text-xs text-slate-500" data-id="element-5817">
              We'll parse every PDF and CSV, then turn each booking into an
              event.
            </p>
          </div>
          <Pill color="coral" data-id="element-5818">★ Workflow #1</Pill>
        </div>
        <div className="border-2 border-dashed border-brand-coral/50 bg-brand-coral/5 rounded-2xl p-8 text-center mb-5" data-id="element-5819">
          <UploadCloudIcon className="w-10 h-10 text-brand-coral mx-auto mb-3" data-id="element-5820" />
          <p className="text-sm font-bold text-brand-navy mb-1" data-id="element-5821">
            Drag & drop PDFs, CSVs, or XLSX
          </p>
          <p className="text-xs text-slate-500" data-id="element-5822">
            or click to browse · up to 200 files at a time
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-5823">
          {[{
          label: 'Q1-2026-bookings.pdf',
          size: '2.4 MB',
          kind: 'PDF'
        }, {
          label: 'lamplighter_bookings.csv',
          size: '184 KB',
          kind: 'CSV'
        }, {
          label: 'wedding-contracts.pdf',
          size: '1.1 MB',
          kind: 'PDF'
        }].map((f, i) => <Card key={i} className="p-3 flex items-center gap-3" data-id="element-5824">
              <div className="w-8 h-8 rounded-lg bg-brand-coral/10 text-brand-coral flex items-center justify-center text-[10px] font-bold" data-id="element-5825">
                {f.kind}
              </div>
              <div className="flex-1 min-w-0" data-id="element-5826">
                <div className="text-xs font-bold text-brand-navy truncate" data-id="element-5827">
                  {f.label}
                </div>
                <div className="text-[10px] text-slate-500" data-id="element-5828">{f.size}</div>
              </div>
              <CheckCircle2Icon className="w-4 h-4 text-green-500 shrink-0" data-id="element-5829" />
            </Card>)}
        </div>
        <button className="mt-5 w-full py-3 bg-brand-coral text-white rounded-xl font-bold text-sm" data-id="element-5830">
          Parse 3 files → Continue
        </button>

        <TutorialBubble b={{
        n: 1,
        text: 'Sales Manager arrives here. Goal: turn the existing book of business into structured events in under 5 minutes.',
        pos: {
          top: '20px',
          right: '24px'
        },
        arrow: 'right',
        color: 'navy'
      }} data-id="element-5831" />
        <TutorialBubble b={{
        n: 2,
        text: 'Drag-drop accepts PDF, CSV, XLSX. Each file gets parsed asynchronously and shows a progress state.',
        pos: {
          top: '120px',
          left: '24px'
        },
        arrow: 'left',
        color: 'coral'
      }} data-id="element-5832" />
        <TutorialBubble b={{
        n: 3,
        text: 'Files are queued for the parser pipeline. PDFs go through OCR + table extraction; CSVs go through column mapping.',
        pos: {
          bottom: '90px',
          right: '20px'
        },
        arrow: 'right',
        color: 'teal'
      }} data-id="element-5833" />
      </div>
    </BrowserChrome>;
}
function P1Screen2() {
  const bookings = [{
    client: 'Acme Corp · Leadership Offsite',
    date: 'Mar 14–16',
    pax: 24,
    value: '$48,200',
    type: 'Corporate · Leadership',
    typeColor: 'coral',
    conf: 96
  }, {
    client: 'Patel–Singh Wedding',
    date: 'Apr 5',
    pax: 180,
    value: '$92,000',
    type: 'Wedding',
    typeColor: 'purple',
    conf: 99
  }, {
    client: 'WNBA West · Training Camp',
    date: 'Apr 12–18',
    pax: 36,
    value: '$58,400',
    type: 'Sports Team',
    typeColor: 'amber',
    conf: 88
  }, {
    client: 'Pacific Tech · Sales Kickoff',
    date: 'Apr 21–22',
    pax: 110,
    value: '$74,800',
    type: 'Corporate · Conference',
    typeColor: 'coral',
    conf: 94
  }, {
    client: "Sarah's 40th Birthday",
    date: 'Apr 29',
    pax: 30,
    value: '$4,200',
    type: 'Birthday · Free Tier',
    typeColor: 'green',
    conf: 92
  }];
  return <BrowserChrome role="Sales Manager" url="offsiteplus.app/sales/review" data-id="element-5834">
      <div className="p-6 relative" data-id="element-5835">
        <div className="flex items-center justify-between mb-4" data-id="element-5836">
          <div data-id="element-5837">
            <h3 className="text-lg font-bold text-brand-navy" data-id="element-5838">
              Review parsed bookings · 5 found
            </h3>
            <p className="text-xs text-slate-500" data-id="element-5839">
              Each row was auto-detected. Confirm or fix the event type before
              we create event records.
            </p>
          </div>
          <button className="text-xs font-bold text-brand-teal" data-id="element-5840">
            Accept all matches above 90% →
          </button>
        </div>
        <Card className="overflow-hidden" data-id="element-5841">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500" data-id="element-5842">
            <div className="col-span-4" data-id="element-5843">Client / Event</div>
            <div className="col-span-2" data-id="element-5844">Date</div>
            <div className="col-span-1 text-center" data-id="element-5845">Pax</div>
            <div className="col-span-2" data-id="element-5846">Value</div>
            <div className="col-span-2" data-id="element-5847">Auto-tag</div>
            <div className="col-span-1 text-right" data-id="element-5848">Conf</div>
          </div>
          {bookings.map((b, i) => <div key={i} className={`grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-slate-100 ${i === 0 ? 'bg-brand-coral/5' : ''}`} data-id="element-5849">
              <div className="col-span-4 text-xs font-bold text-brand-navy truncate" data-id="element-5850">
                {b.client}
              </div>
              <div className="col-span-2 text-xs text-slate-600" data-id="element-5851">{b.date}</div>
              <div className="col-span-1 text-xs text-slate-600 text-center" data-id="element-5852">
                {b.pax}
              </div>
              <div className="col-span-2 text-xs font-semibold text-slate-800" data-id="element-5853">
                {b.value}
              </div>
              <div className="col-span-2" data-id="element-5854">
                <Pill color={b.typeColor as any} data-id="element-5855">{b.type}</Pill>
              </div>
              <div className="col-span-1 text-[10px] font-bold text-green-600 text-right" data-id="element-5856">
                {b.conf}%
              </div>
            </div>)}
        </Card>
        <div className="flex items-center justify-between mt-4" data-id="element-5857">
          <div className="text-xs text-slate-500" data-id="element-5858">
            5 of 5 above confidence threshold · 1 needs review
          </div>
          <button className="px-5 py-2.5 bg-brand-coral text-white rounded-xl font-bold text-sm" data-id="element-5859">
            Create 5 events →
          </button>
        </div>

        <TutorialBubble b={{
        n: 1,
        text: 'Each booking now has: client, date, headcount, contract value, auto-detected event type, and a confidence score.',
        pos: {
          top: '20px',
          right: '24px'
        },
        arrow: 'right',
        color: 'navy'
      }} data-id="element-5860" />
        <TutorialBubble b={{
        n: 2,
        text: 'Highlighted row is the active one. Manager can click any row to inspect / fix the detected fields.',
        pos: {
          top: '155px',
          left: '24px'
        },
        arrow: 'left',
        color: 'coral'
      }} data-id="element-5861" />
        <TutorialBubble b={{
        n: 3,
        text: 'Confidence <85% surfaces a manual review flag. The LLM tiebreaker explains its reasoning when challenged.',
        pos: {
          top: '240px',
          right: '20px'
        },
        arrow: 'right',
        color: 'teal'
      }} data-id="element-5862" />
        <TutorialBubble b={{
        n: 4,
        text: 'One click commits every approved row to the events table — instantly populating the pipeline.',
        pos: {
          bottom: '24px',
          right: '24px'
        },
        arrow: 'bottom',
        color: 'amber'
      }} data-id="element-5863" />
      </div>
    </BrowserChrome>;
}
function P1Screen3() {
  return <BrowserChrome role="Sales Manager" url="offsiteplus.app/events/EVT-2641/package" data-id="element-5864">
      <div className="p-6 relative" data-id="element-5865">
        <div className="flex items-center justify-between mb-5" data-id="element-5866">
          <div data-id="element-5867">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1" data-id="element-5868">
              Acme Corp · Leadership Offsite · Mar 14–16 · 24 pax
            </div>
            <h3 className="text-lg font-bold text-brand-navy" data-id="element-5869">
              Pick the right package
            </h3>
          </div>
          <Pill color="coral" data-id="element-5870">Step 3 of 4</Pill>
        </div>
        <div className="bg-gradient-to-br from-brand-coral/10 to-brand-coral/5 border-2 border-brand-coral rounded-2xl p-5 mb-4 relative" data-id="element-5871">
          <div className="absolute -top-2 left-5 text-[10px] font-bold bg-brand-coral text-white px-2 py-0.5 rounded-full" data-id="element-5872">
            ★ AI Recommendation
          </div>
          <div className="flex items-start gap-4" data-id="element-5873">
            <div className="w-12 h-12 rounded-xl bg-brand-coral text-white flex items-center justify-center font-black" data-id="element-5874">
              P+
            </div>
            <div className="flex-1" data-id="element-5875">
              <h4 className="text-base font-bold text-brand-navy mb-1" data-id="element-5876">
                Premium Offsite Plus · $12,500
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed mb-2" data-id="element-5877">
                Best fit: 24 executive pax, 3-day duration, contract value $48k.
                Mirrors 12 past Lamplighter wins.
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-5878">
                <Pill color="coral" data-id="element-5879">Custom journey</Pill>
                <Pill color="coral" data-id="element-5880">Facilitator modules</Pill>
                <Pill color="coral" data-id="element-5881">30/60/90 follow-up</Pill>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-5" data-id="element-5882">
          {[{
          name: 'Basic Hub',
          price: 'Included'
        }, {
          name: 'Hub Lite',
          price: '$299'
        }, {
          name: 'Smart Upgrade',
          price: '$2,500'
        }].map((p, i) => <Card key={i} className="p-3 text-center hover:border-slate-400 cursor-pointer transition-colors" data-id="element-5883">
              <div className="text-xs font-bold text-slate-700" data-id="element-5884">{p.name}</div>
              <div className="text-[10px] text-slate-500" data-id="element-5885">{p.price}</div>
            </Card>)}
        </div>
        <button className="w-full py-3 bg-brand-coral text-white rounded-xl font-bold text-sm" data-id="element-5886">
          Accept Premium → Build proposal
        </button>

        <TutorialBubble b={{
        n: 1,
        text: 'AI scores each package against the booking. Premium wins because of executive pax + multi-day + contract value.',
        pos: {
          top: '110px',
          right: '20px'
        },
        arrow: 'right',
        color: 'navy'
      }} data-id="element-5887" />
        <TutorialBubble b={{
        n: 2,
        text: 'Tap any lower-tier card to override. The override + reason gets logged in the audit trail.',
        pos: {
          bottom: '120px',
          left: '20px'
        },
        arrow: 'left',
        color: 'coral'
      }} data-id="element-5888" />
        <TutorialBubble b={{
        n: 3,
        text: 'Selecting a package locks the feature_flags on the event record — what modules show up on the hub.',
        pos: {
          bottom: '40px',
          right: '20px'
        },
        arrow: 'bottom',
        color: 'teal'
      }} data-id="element-5889" />
      </div>
    </BrowserChrome>;
}
function P1Screen4() {
  return <BrowserChrome role="Sales Manager" url="offsiteplus.app/events/EVT-2641/proposal" data-id="element-5890">
      <div className="p-6 relative" data-id="element-5891">
        <div className="flex items-center justify-between mb-4" data-id="element-5892">
          <div data-id="element-5893">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1" data-id="element-5894">
              Acme Corp · Premium Offsite Plus
            </div>
            <h3 className="text-lg font-bold text-brand-navy" data-id="element-5895">
              AI proposal · ready to send
            </h3>
          </div>
          <Pill color="coral" data-id="element-5896">Generated in 23s</Pill>
        </div>
        <div className="grid grid-cols-5 gap-4" data-id="element-5897">
          <Card className="col-span-3 p-5" data-id="element-5898">
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-coral mb-1" data-id="element-5899">
              Proposal Preview
            </div>
            <h4 className="text-sm font-bold text-brand-navy mb-2" data-id="element-5900">
              Three days designed to align your leadership team
            </h4>
            <div className="space-y-1.5 text-[11px] text-slate-600 leading-relaxed" data-id="element-5901">
              <p data-id="element-5902">
                <span className="font-bold text-brand-navy" data-id="element-5903">Day 1.</span>{' '}
                Arrival · diagnostic intake · welcome dinner.
              </p>
              <p data-id="element-5904">
                <span className="font-bold text-brand-navy" data-id="element-5905">Day 2.</span>{' '}
                Strategy block · alignment workshop · group dinner.
              </p>
              <p data-id="element-5906">
                <span className="font-bold text-brand-navy" data-id="element-5907">Day 3.</span> Action
                commitments · 30/60/90 plan · departures.
              </p>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-3 text-[10px]" data-id="element-5908">
              <div data-id="element-5909">
                <div className="text-slate-400 uppercase font-bold mb-0.5" data-id="element-5910">
                  Investment
                </div>
                <div className="font-bold text-brand-navy" data-id="element-5911">$12,500</div>
              </div>
              <div data-id="element-5912">
                <div className="text-slate-400 uppercase font-bold mb-0.5" data-id="element-5913">
                  Pax
                </div>
                <div className="font-bold text-brand-navy" data-id="element-5914">24</div>
              </div>
              <div data-id="element-5915">
                <div className="text-slate-400 uppercase font-bold mb-0.5" data-id="element-5916">
                  Cadence
                </div>
                <div className="font-bold text-brand-navy" data-id="element-5917">3 days</div>
              </div>
            </div>
          </Card>
          <Card className="col-span-2 p-5 bg-slate-50" data-id="element-5918">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3" data-id="element-5919">
              Actions
            </div>
            <div className="space-y-2" data-id="element-5920">
              <button className="w-full py-2 bg-brand-coral text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1.5" data-id="element-5921">
                <SendIcon className="w-3.5 h-3.5" data-id="element-5922" /> Send to client
              </button>
              <button className="w-full py-2 bg-white border border-slate-200 text-brand-navy rounded-lg font-bold text-xs" data-id="element-5923">
                Copy shareable link
              </button>
              <button className="w-full py-2 bg-white border border-slate-200 text-brand-navy rounded-lg font-bold text-xs" data-id="element-5924">
                Download PDF
              </button>
              <button className="w-full py-2 bg-white border border-slate-200 text-slate-500 rounded-lg font-bold text-xs" data-id="element-5925">
                Regenerate with notes
              </button>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 text-[10px] text-slate-500" data-id="element-5926">
              <div className="font-bold text-slate-700 mb-1" data-id="element-5927">View tracking</div>
              <p data-id="element-5928">Get notified when the client opens the proposal link.</p>
            </div>
          </Card>
        </div>

        <TutorialBubble b={{
        n: 1,
        text: 'AI drafts a cover letter, day-by-day breakdown, and pricing in under 30s. Brand kit applied automatically.',
        pos: {
          top: '110px',
          left: '24px'
        },
        arrow: 'left',
        color: 'navy'
      }} data-id="element-5929" />
        <TutorialBubble b={{
        n: 2,
        text: 'Status starts as Draft. Coordinator approval required before sending — that gate is enforced server-side.',
        pos: {
          top: '160px',
          right: '20px'
        },
        arrow: 'right',
        color: 'coral'
      }} data-id="element-5930" />
        <TutorialBubble b={{
        n: 3,
        text: 'Shareable link tracks every open. View events flow into the pipeline dashboard for follow-up nudges.',
        pos: {
          bottom: '40px',
          right: '20px'
        },
        arrow: 'bottom',
        color: 'teal'
      }} data-id="element-5931" />
      </div>
    </BrowserChrome>;
}
/* ----- P2 · Event Coordinator ----- */
function P2Screen1() {
  return <BrowserChrome role="Event Coordinator" url="offsiteplus.app/coordinator/events" data-id="element-5932">
      <div className="p-6 relative" data-id="element-5933">
        <div className="flex items-center justify-between mb-4" data-id="element-5934">
          <h3 className="text-lg font-bold text-brand-navy" data-id="element-5935">
            My active events
          </h3>
          <Pill color="teal" data-id="element-5936">8 events · 3 this week</Pill>
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-5937">
          {[{
          name: 'Acme Corp · Leadership',
          date: 'Mar 14–16',
          pax: 24,
          status: 'Confirmed',
          statusColor: 'amber',
          progress: 60
        }, {
          name: 'Patel–Singh Wedding',
          date: 'Apr 5',
          pax: 180,
          status: 'Live',
          statusColor: 'green',
          progress: 92
        }, {
          name: 'WNBA West Camp',
          date: 'Apr 12–18',
          pax: 36,
          status: 'Draft',
          statusColor: 'slate',
          progress: 25
        }, {
          name: 'Pacific Tech SKO',
          date: 'Apr 21–22',
          pax: 110,
          status: 'Confirmed',
          statusColor: 'amber',
          progress: 75
        }].map((e, i) => <Card key={i} className="p-4" data-id="element-5938">
              <div className="flex items-start justify-between mb-2" data-id="element-5939">
                <h4 className="text-sm font-bold text-brand-navy" data-id="element-5940">{e.name}</h4>
                <Pill color={e.statusColor as any} data-id="element-5941">{e.status}</Pill>
              </div>
              <div className="text-[11px] text-slate-500 mb-3" data-id="element-5942">
                {e.date} · {e.pax} pax
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-1" data-id="element-5943">
                <div className="h-full bg-brand-teal" style={{
              width: `${e.progress}%`
            }} data-id="element-5944" />
              </div>
              <div className="text-[10px] text-slate-500" data-id="element-5945">
                Setup {e.progress}% complete
              </div>
            </Card>)}
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Coordinator dashboard. Every event the Sales Manager handed off shows up here automatically.',
        pos: {
          top: '20px',
          right: '24px'
        },
        arrow: 'right',
        color: 'navy'
      }} data-id="element-5946" />
        <TutorialBubble b={{
        n: 2,
        text: 'Status pill = event lifecycle state (Draft → Confirmed → Live → Completed → Archived).',
        pos: {
          top: '140px',
          left: '24px'
        },
        arrow: 'left',
        color: 'teal'
      }} data-id="element-5947" />
        <TutorialBubble b={{
        n: 3,
        text: 'Progress = computed setup completeness. Agenda, materials, attendees, checklist — each fills the bar.',
        pos: {
          bottom: '60px',
          right: '20px'
        },
        arrow: 'right',
        color: 'coral'
      }} data-id="element-5948" />
      </div>
    </BrowserChrome>;
}
function P2Screen2() {
  const blocks = [{
    time: '09:00',
    title: 'Opening Keynote',
    host: 'CEO',
    pax: 24,
    color: 'bg-brand-coral'
  }, {
    time: '10:30',
    title: 'Strategy Workshop',
    host: 'Facilitator',
    pax: 24,
    color: 'bg-brand-teal'
  }, {
    time: '12:30',
    title: 'Lunch · Vineyard',
    host: '—',
    pax: 24,
    color: 'bg-amber-400'
  }, {
    time: '14:00',
    title: 'Breakout Pods',
    host: 'Self-led',
    pax: 24,
    color: 'bg-purple-500'
  }, {
    time: '16:00',
    title: 'Action Commitments',
    host: 'CEO',
    pax: 24,
    color: 'bg-brand-coral'
  }];
  return <BrowserChrome role="Event Coordinator" url="offsiteplus.app/events/EVT-2641/agenda" data-id="element-5949">
      <div className="p-6 relative" data-id="element-5950">
        <div className="flex items-center justify-between mb-4" data-id="element-5951">
          <h3 className="text-lg font-bold text-brand-navy" data-id="element-5952">
            Agenda · Day 2 of 3
          </h3>
          <div className="flex gap-1.5" data-id="element-5953">
            <Pill color="teal" data-id="element-5954">Day 1</Pill>
            <Pill color="coral" data-id="element-5955">Day 2</Pill>
            <Pill color="slate" data-id="element-5956">Day 3</Pill>
          </div>
        </div>
        <Card className="p-3 mb-3 border-dashed border-2 border-slate-200 bg-slate-50 text-center text-[11px] text-slate-500" data-id="element-5957">
          + Add block · or drag from template
        </Card>
        <div className="space-y-2" data-id="element-5958">
          {blocks.map((b, i) => <Card key={i} className="p-3 flex items-center gap-3" data-id="element-5959">
              <div className={`w-1.5 self-stretch rounded-full ${b.color}`} data-id="element-5960" />
              <div className="text-xs font-mono font-bold text-slate-500 w-12" data-id="element-5961">
                {b.time}
              </div>
              <div className="flex-1" data-id="element-5962">
                <div className="text-sm font-bold text-brand-navy leading-tight" data-id="element-5963">
                  {b.title}
                </div>
                <div className="text-[10px] text-slate-500" data-id="element-5964">
                  Host · {b.host} · {b.pax} pax
                </div>
              </div>
              <button className="text-[10px] text-slate-400 hover:text-brand-coral" data-id="element-5965">
                Edit
              </button>
            </Card>)}
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Day tabs let you build a multi-day event. Each day has its own agenda blocks and timing.',
        pos: {
          top: '20px',
          right: '24px'
        },
        arrow: 'right',
        color: 'navy'
      }} data-id="element-5966" />
        <TutorialBubble b={{
        n: 2,
        text: 'Drag-drop blocks · pull from 8 starter templates (one per event type) or build from scratch.',
        pos: {
          top: '110px',
          left: '24px'
        },
        arrow: 'left',
        color: 'teal'
      }} data-id="element-5967" />
        <TutorialBubble b={{
        n: 3,
        text: 'Color = block type (keynote / workshop / meal / breakout). Host assignment flags facilitator visibility.',
        pos: {
          bottom: '60px',
          right: '20px'
        },
        arrow: 'right',
        color: 'coral'
      }} data-id="element-5968" />
      </div>
    </BrowserChrome>;
}
function P2Screen3() {
  return <BrowserChrome role="Event Coordinator" url="offsiteplus.app/events/EVT-2641/materials" data-id="element-5969">
      <div className="p-6 relative" data-id="element-5970">
        <div className="flex items-center justify-between mb-4" data-id="element-5971">
          <h3 className="text-lg font-bold text-brand-navy" data-id="element-5972">
            Materials & vendors
          </h3>
          <button className="text-xs font-bold text-brand-teal" data-id="element-5973">
            + Upload material
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-5974">
          <Card className="p-4" data-id="element-5975">
            <div className="text-[10px] font-bold uppercase tracking-wider text-pink-600 mb-3 flex items-center gap-1.5" data-id="element-5976">
              <ImageIcon className="w-3 h-3" data-id="element-5977" /> Video · Mux
            </div>
            <div className="space-y-2" data-id="element-5978">
              {['CEO welcome.mp4 · 4:32', 'Brand reel.mp4 · 1:18', 'Day 1 highlights.mp4 · 2:44'].map((m, i) => <div key={i} className="flex items-center gap-2 p-2 bg-pink-50 rounded text-[11px] text-slate-700" data-id="element-5979">
                  <div className="w-6 h-6 rounded bg-pink-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5980">
                    ▶
                  </div>
                  {m}
                </div>)}
            </div>
          </Card>
          <Card className="p-4" data-id="element-5981">
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-teal mb-3" data-id="element-5982">
              Files & links
            </div>
            <div className="space-y-2" data-id="element-5983">
              {[{
              name: 'Pre-read.pdf',
              size: '1.2 MB'
            }, {
              name: 'Workbook.pdf',
              size: '4.8 MB'
            }, {
              name: 'Venue map.png',
              size: '340 KB'
            }, {
              name: 'Dress code.pdf',
              size: '120 KB'
            }].map((f, i) => <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-[11px]" data-id="element-5984">
                  <span className="text-brand-navy font-medium" data-id="element-5985">{f.name}</span>
                  <span className="text-slate-400" data-id="element-5986">{f.size}</span>
                </div>)}
            </div>
          </Card>
        </div>
        <Card className="p-4 mt-3" data-id="element-5987">
          <div className="text-[10px] font-bold uppercase tracking-wider text-purple-700 mb-3" data-id="element-5988">
            Vendors
          </div>
          <div className="grid grid-cols-3 gap-2" data-id="element-5989">
            {['Vineyard Catering', 'Sunrise Transport', 'Local DJ · K. Mendez'].map((v, i) => <div key={i} className="p-2 bg-purple-50 rounded text-[11px] text-purple-800 font-medium" data-id="element-5990">
                {v}
              </div>)}
          </div>
        </Card>
        <TutorialBubble b={{
        n: 1,
        text: 'Mux pipeline: Direct Upload URL → encoding → mux_asset_id + mux_playback_id stored on materials row.',
        pos: {
          top: '110px',
          left: '24px'
        },
        arrow: 'left',
        color: 'pink'
      }} data-id="element-5991" />
        <TutorialBubble b={{
        n: 2,
        text: 'Files use S3-backed storage. Each material has visibility (public / attendees / staff).',
        pos: {
          top: '110px',
          right: '24px'
        },
        arrow: 'right',
        color: 'teal'
      }} data-id="element-5992" />
        <TutorialBubble b={{
        n: 3,
        text: 'Vendor table links to the event. Coordinator can attach contracts and confirm timing per vendor.',
        pos: {
          bottom: '40px',
          right: '20px'
        },
        arrow: 'bottom',
        color: 'purple'
      }} data-id="element-5993" />
      </div>
    </BrowserChrome>;
}
function P2Screen4() {
  return <BrowserChrome role="Event Coordinator" url="offsiteplus.app/events/EVT-2641/checklist" data-id="element-5994">
      <div className="p-6 relative" data-id="element-5995">
        <div className="flex items-center justify-between mb-4" data-id="element-5996">
          <h3 className="text-lg font-bold text-brand-navy" data-id="element-5997">
            Event-day checklist
          </h3>
          <Pill color="green" data-id="element-5998">12 / 18 complete</Pill>
        </div>
        <div className="space-y-2" data-id="element-5999">
          {[{
          done: true,
          text: 'Rooms blocked & confirmed',
          time: '07:00'
        }, {
          done: true,
          text: 'Breakfast service setup',
          time: '07:30'
        }, {
          done: true,
          text: 'AV check in main room',
          time: '08:00'
        }, {
          done: false,
          text: 'Facilitator arrival briefing',
          time: '08:30',
          active: true
        }, {
          done: false,
          text: 'Welcome materials at registration',
          time: '08:45'
        }, {
          done: false,
          text: 'Day 1 broadcast to attendees',
          time: '09:00'
        }].map((c, i) => <Card key={i} className={`p-3 flex items-center gap-3 ${c.active ? 'border-brand-coral border-2' : ''}`} data-id="element-6000">
              <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${c.done ? 'bg-green-500' : 'bg-white border-2 border-slate-300'}`} data-id="element-6001">
                {c.done && <CheckCircle2Icon className="w-3 h-3 text-white" data-id="element-6002" />}
              </div>
              <div className="flex-1" data-id="element-6003">
                <div className={`text-xs font-bold ${c.done ? 'text-slate-400 line-through' : 'text-brand-navy'}`} data-id="element-6004">
                  {c.text}
                </div>
              </div>
              <code className="text-[10px] font-mono text-slate-500" data-id="element-6005">
                {c.time}
              </code>
            </Card>)}
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Coordinator marks tasks off in real-time. Each check stamps audit_log with actor + timestamp.',
        pos: {
          top: '160px',
          right: '20px'
        },
        arrow: 'right',
        color: 'teal'
      }} data-id="element-6006" />
        <TutorialBubble b={{
        n: 2,
        text: 'Active task highlighted = "next up." Push notification fires if a task slips past its scheduled time.',
        pos: {
          top: '230px',
          left: '24px'
        },
        arrow: 'left',
        color: 'coral'
      }} data-id="element-6007" />
      </div>
    </BrowserChrome>;
}
/* ----- P3 · Client / Organizer ----- */
function P3Screen1() {
  return <BrowserChrome role="Client / Organizer" url="hub.acme.events/customize" data-id="element-6008">
      <div className="p-6 relative" data-id="element-6009">
        <div className="flex items-center justify-between mb-4" data-id="element-6010">
          <h3 className="text-lg font-bold text-brand-navy" data-id="element-6011">
            Customize your hub
          </h3>
          <Pill color="purple" data-id="element-6012">Client view</Pill>
        </div>
        <div className="grid grid-cols-5 gap-3" data-id="element-6013">
          <Card className="col-span-2 p-4" data-id="element-6014">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3" data-id="element-6015">
              Brand kit
            </div>
            <div className="space-y-3" data-id="element-6016">
              <div data-id="element-6017">
                <div className="text-[10px] text-slate-500 mb-1" data-id="element-6018">Logo</div>
                <div className="h-12 bg-slate-100 rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400" data-id="element-6019">
                  Drop logo
                </div>
              </div>
              <div data-id="element-6020">
                <div className="text-[10px] text-slate-500 mb-1" data-id="element-6021">
                  Accent color
                </div>
                <div className="flex gap-1.5" data-id="element-6022">
                  {['bg-brand-coral', 'bg-brand-teal', 'bg-purple-500', 'bg-amber-500', 'bg-brand-navy'].map((c, i) => <button key={i} className={`w-7 h-7 rounded-lg ${c} ${i === 0 ? 'ring-2 ring-offset-2 ring-brand-navy' : ''}`} data-id="element-6023" />)}
                </div>
              </div>
              <div data-id="element-6024">
                <div className="text-[10px] text-slate-500 mb-1" data-id="element-6025">
                  Welcome message
                </div>
                <div className="text-[11px] p-2 bg-slate-50 rounded border border-slate-200 text-slate-600" data-id="element-6026">
                  Welcome to our 2026 Leadership Offsite...
                </div>
              </div>
            </div>
          </Card>
          <Card className="col-span-3 p-0 overflow-hidden" data-id="element-6027">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 pt-3 mb-2" data-id="element-6028">
              Live preview · what attendees see
            </div>
            <div className="bg-gradient-to-br from-brand-coral/10 via-white to-brand-coral/5 px-4 py-5" data-id="element-6029">
              <div className="text-2xl font-bold text-brand-navy mb-1 font-serif" data-id="element-6030">
                Acme Leadership
              </div>
              <div className="text-xs text-slate-600 mb-3" data-id="element-6031">
                Mar 14–16 · 24 leaders
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200 mb-2" data-id="element-6032">
                <div className="text-[10px] font-bold text-brand-coral uppercase" data-id="element-6033">
                  Today · 10:30 AM
                </div>
                <div className="text-xs font-bold text-brand-navy" data-id="element-6034">
                  Strategy Workshop
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200" data-id="element-6035">
                <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-6036">
                  Next · 12:30 PM
                </div>
                <div className="text-xs text-slate-700" data-id="element-6037">Lunch · Vineyard</div>
              </div>
            </div>
          </Card>
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Brand kit fields write to event.theme_config. Every guest hub re-renders instantly when changed.',
        pos: {
          top: '100px',
          left: '24px'
        },
        arrow: 'left',
        color: 'purple'
      }} data-id="element-6038" />
        <TutorialBubble b={{
        n: 2,
        text: 'Live preview = exactly what an Attendee sees. Toggle device width to test mobile.',
        pos: {
          top: '110px',
          right: '24px'
        },
        arrow: 'right',
        color: 'coral'
      }} data-id="element-6039" />
      </div>
    </BrowserChrome>;
}
function P3Screen2() {
  return <BrowserChrome role="Client / Organizer" url="hub.acme.events/guests" data-id="element-6040">
      <div className="p-6 relative" data-id="element-6041">
        <div className="flex items-center justify-between mb-4" data-id="element-6042">
          <h3 className="text-lg font-bold text-brand-navy" data-id="element-6043">
            Guest list · 24 invited
          </h3>
          <div className="flex gap-2" data-id="element-6044">
            <button className="text-xs font-bold text-brand-teal" data-id="element-6045">
              Import CSV
            </button>
            <button className="text-xs font-bold text-brand-coral" data-id="element-6046">
              + Add guest
            </button>
          </div>
        </div>
        <div className="flex gap-2 mb-3" data-id="element-6047">
          <Pill color="coral" data-id="element-6048">All 24</Pill>
          <Pill color="amber" data-id="element-6049">VIP 4</Pill>
          <Pill color="slate" data-id="element-6050">Standard 20</Pill>
          <Pill color="green" data-id="element-6051">Confirmed 22</Pill>
        </div>
        <Card className="overflow-hidden" data-id="element-6052">
          {[{
          name: 'Sarah Chen',
          tier: 'VIP',
          rsvp: 'Confirmed',
          email: 's.chen@acme.com'
        }, {
          name: 'Marcus Webb',
          tier: 'VIP',
          rsvp: 'Confirmed',
          email: 'm.webb@acme.com'
        }, {
          name: 'Priya Shah',
          tier: 'Standard',
          rsvp: 'Confirmed',
          email: 'p.shah@acme.com'
        }, {
          name: 'James Lin',
          tier: 'Standard',
          rsvp: 'Pending',
          email: 'j.lin@acme.com'
        }].map((g, i) => <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center border-b border-slate-100 last:border-0 text-xs" data-id="element-6053">
              <div className="col-span-1" data-id="element-6054">
                <div className="w-7 h-7 rounded-full bg-brand-teal/20 text-brand-teal flex items-center justify-center text-[10px] font-bold" data-id="element-6055">
                  {g.name.split(' ').map(s => s[0]).join('')}
                </div>
              </div>
              <div className="col-span-4" data-id="element-6056">
                <div className="font-bold text-brand-navy" data-id="element-6057">{g.name}</div>
                <div className="text-[10px] text-slate-500" data-id="element-6058">{g.email}</div>
              </div>
              <div className="col-span-3" data-id="element-6059">
                <Pill color={g.tier === 'VIP' ? 'amber' : 'slate'} data-id="element-6060">
                  {g.tier}
                </Pill>
              </div>
              <div className="col-span-3" data-id="element-6061">
                <Pill color={g.rsvp === 'Confirmed' ? 'green' : 'slate'} data-id="element-6062">
                  {g.rsvp}
                </Pill>
              </div>
              <div className="col-span-1 text-right text-slate-400 text-[10px]" data-id="element-6063">
                Edit
              </div>
            </div>)}
        </Card>
        <TutorialBubble b={{
        n: 1,
        text: 'Tiers control what each attendee sees on the hub. VIPs get private materials and personal welcome notes.',
        pos: {
          top: '100px',
          right: '20px'
        },
        arrow: 'right',
        color: 'amber'
      }} data-id="element-6064" />
        <TutorialBubble b={{
        n: 2,
        text: 'CSV import handles headcount lists from the client. Email triggers magic-link invites automatically.',
        pos: {
          top: '20px',
          right: '120px'
        },
        arrow: 'top',
        color: 'purple'
      }} data-id="element-6065" />
      </div>
    </BrowserChrome>;
}
function P3Screen3() {
  return <BrowserChrome role="Client / Organizer" url="hub.acme.events/approve" data-id="element-6066">
      <div className="p-6 relative" data-id="element-6067">
        <div className="flex items-center justify-between mb-4" data-id="element-6068">
          <h3 className="text-lg font-bold text-brand-navy" data-id="element-6069">
            Approval queue · 4 items
          </h3>
          <Pill color="coral" data-id="element-6070">AI drafted</Pill>
        </div>
        <div className="space-y-2" data-id="element-6071">
          {[{
          kind: 'Welcome email',
          draft: '"Welcome team — we can\'t wait to host you in Sonoma..."',
          ready: true
        }, {
          kind: 'Day 1 morning prompt',
          draft: '"What is one outcome you would call a win by Friday?"',
          ready: true
        }, {
          kind: 'Workshop pre-read',
          draft: 'AI-generated briefing on strategy framework (2 pages)',
          ready: false
        }, {
          kind: 'Vineyard lunch note',
          draft: '"Reminder: vegan and gluten-free options confirmed..."',
          ready: true
        }].map((a, i) => <Card key={i} className="p-3" data-id="element-6072">
              <div className="flex items-start gap-3" data-id="element-6073">
                <div className="flex-1" data-id="element-6074">
                  <div className="flex items-center gap-2 mb-1" data-id="element-6075">
                    <Pill color="purple" data-id="element-6076">{a.kind}</Pill>
                    {!a.ready && <Pill color="amber" data-id="element-6077">Needs your input</Pill>}
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed" data-id="element-6078">
                    {a.draft}
                  </p>
                </div>
                <div className="flex gap-1.5" data-id="element-6079">
                  <button className="px-2.5 py-1 bg-green-500 text-white rounded-lg text-[10px] font-bold" data-id="element-6080">
                    Approve
                  </button>
                  <button className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 rounded-lg text-[10px] font-bold" data-id="element-6081">
                    Edit
                  </button>
                </div>
              </div>
            </Card>)}
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Every AI-generated item lands in Draft status. Nothing publishes to attendees without a human approval.',
        pos: {
          top: '20px',
          right: '24px'
        },
        arrow: 'right',
        color: 'navy'
      }} data-id="element-6082" />
        <TutorialBubble b={{
        n: 2,
        text: 'Approving creates a Published version with a content_version row — rollback always available.',
        pos: {
          top: '160px',
          left: '24px'
        },
        arrow: 'left',
        color: 'purple'
      }} data-id="element-6083" />
      </div>
    </BrowserChrome>;
}
function P3Screen4() {
  return <BrowserChrome role="Client / Organizer" url="hub.acme.events/broadcast" data-id="element-6084">
      <div className="p-6 relative" data-id="element-6085">
        <div className="flex items-center justify-between mb-4" data-id="element-6086">
          <h3 className="text-lg font-bold text-brand-navy" data-id="element-6087">
            Send update to attendees
          </h3>
          <Pill color="teal" data-id="element-6088">24 recipients</Pill>
        </div>
        <Card className="p-4 mb-3" data-id="element-6089">
          <div className="grid grid-cols-3 gap-2 mb-3" data-id="element-6090">
            {['Email', 'Push', 'SMS'].map((c, i) => <button key={i} className={`py-2 rounded-lg text-xs font-bold ${i === 0 ? 'bg-brand-coral text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-6091">
                {c}
              </button>)}
          </div>
          <div className="mb-3" data-id="element-6092">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1" data-id="element-6093">
              Subject
            </div>
            <div className="text-sm font-bold text-brand-navy p-2 bg-slate-50 rounded border border-slate-200" data-id="element-6094">
              Day 2 schedule update + lunch logistics
            </div>
          </div>
          <div data-id="element-6095">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1" data-id="element-6096">
              Message
            </div>
            <div className="text-xs text-slate-700 p-3 bg-slate-50 rounded border border-slate-200 leading-relaxed" data-id="element-6097">
              Hi {'{first_name}'}, quick update on tomorrow. The workshop starts
              at 10:30 (was 11:00). Vineyard lunch ride leaves promptly at
              12:15...
            </div>
          </div>
        </Card>
        <div className="flex items-center justify-between" data-id="element-6098">
          <div className="text-[10px] text-slate-500 flex items-center gap-1.5" data-id="element-6099">
            <ShieldCheckIcon className="w-3 h-3" data-id="element-6100" /> Opt-outs respected · logged
            in messages table
          </div>
          <button className="px-5 py-2.5 bg-brand-coral text-white rounded-xl font-bold text-sm flex items-center gap-1.5" data-id="element-6101">
            <SendIcon className="w-3.5 h-3.5" data-id="element-6102" /> Send to 24
          </button>
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Multi-channel broadcast: Email, Push, or SMS — each writes a row to messages with channel + status.',
        pos: {
          top: '100px',
          left: '24px'
        },
        arrow: 'left',
        color: 'purple'
      }} data-id="element-6103" />
        <TutorialBubble b={{
        n: 2,
        text: 'Personalization tokens like {first_name} pull from the attendees table at send-time.',
        pos: {
          top: '220px',
          right: '20px'
        },
        arrow: 'right',
        color: 'teal'
      }} data-id="element-6104" />
      </div>
    </BrowserChrome>;
}
/* ----- P4 · Attendee / Guest ----- */
function P4Screen1() {
  return <BrowserChrome role="Attendee · Mobile" url="hub.acme.events/welcome" data-id="element-6105">
      <div className="p-6 relative flex justify-center" data-id="element-6106">
        <div className="w-72 bg-white rounded-3xl border-4 border-slate-900 shadow-2xl overflow-hidden" data-id="element-6107">
          <div className="bg-slate-900 h-6 flex items-center justify-center" data-id="element-6108">
            <div className="w-12 h-1 bg-slate-700 rounded-full" data-id="element-6109" />
          </div>
          <div className="bg-gradient-to-br from-brand-coral/10 to-white p-5" data-id="element-6110">
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-coral mb-1" data-id="element-6111">
              Welcome to Sonoma
            </div>
            <h4 className="text-xl font-bold text-brand-navy mb-1 font-serif" data-id="element-6112">
              Acme Leadership 2026
            </h4>
            <p className="text-xs text-slate-600 mb-4" data-id="element-6113">
              Hi Sarah — your offsite starts in 4 hours.
            </p>
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3" data-id="element-6114">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2" data-id="element-6115">
                Scan for instant entry
              </div>
              <div className="w-32 h-32 bg-slate-900 mx-auto rounded-lg grid grid-cols-8 grid-rows-8 gap-0.5 p-1" data-id="element-6116">
                {Array.from({
                length: 64
              }, (_, i) => <div key={i} className={`${Math.random() > 0.5 ? 'bg-white' : 'bg-transparent'}`} data-id="element-6117" />)}
              </div>
            </div>
            <button className="w-full py-3 bg-brand-coral text-white rounded-xl font-bold text-xs mb-2" data-id="element-6118">
              See full schedule
            </button>
            <button className="w-full py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-xs" data-id="element-6119">
              Hotel info & directions
            </button>
          </div>
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Attendee arrives from a magic-link or QR scan. No password. Auth happens with a signed token + attendee_id.',
        pos: {
          top: '40px',
          left: '24px'
        },
        arrow: 'left',
        color: 'amber'
      }} data-id="element-6120" />
        <TutorialBubble b={{
        n: 2,
        text: 'Personalization pulled from attendees table — name, tier, dietary, room — surfaced where it matters.',
        pos: {
          top: '120px',
          right: '24px'
        },
        arrow: 'right',
        color: 'coral'
      }} data-id="element-6121" />
        <TutorialBubble b={{
        n: 3,
        text: 'QR is unique per attendee. One scan = check-in row + audit log + push notification to Coordinator.',
        pos: {
          bottom: '80px',
          right: '20px'
        },
        arrow: 'right',
        color: 'teal'
      }} data-id="element-6122" />
      </div>
    </BrowserChrome>;
}
function P4Screen2() {
  return <BrowserChrome role="Attendee · Mobile" url="hub.acme.events/now" data-id="element-6123">
      <div className="p-6 relative flex justify-center" data-id="element-6124">
        <div className="w-72 bg-white rounded-3xl border-4 border-slate-900 shadow-2xl overflow-hidden" data-id="element-6125">
          <div className="bg-slate-900 h-6 flex items-center justify-center" data-id="element-6126">
            <div className="w-12 h-1 bg-slate-700 rounded-full" data-id="element-6127" />
          </div>
          <div className="p-5 bg-slate-50" data-id="element-6128">
            <div className="bg-brand-coral text-white rounded-2xl p-4 mb-3 relative overflow-hidden" data-id="element-6129">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full blur-2xl" data-id="element-6130" />
              <div className="text-[10px] font-bold uppercase tracking-wider text-yellow-200 mb-1" data-id="element-6131">
                ● Live now · ends 12:00
              </div>
              <h4 className="text-base font-bold mb-1" data-id="element-6132">Strategy Workshop</h4>
              <p className="text-[11px] opacity-90" data-id="element-6133">
                Main Room · with Facilitator J. Wei
              </p>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2" data-id="element-6134">
              Up next
            </div>
            <div className="space-y-2 mb-4" data-id="element-6135">
              {[{
              time: '12:30',
              title: 'Lunch · Vineyard',
              host: 'Bus from lobby'
            }, {
              time: '14:00',
              title: 'Breakout Pods',
              host: '4 rooms'
            }, {
              time: '16:00',
              title: 'Action Commitments',
              host: 'Main Room'
            }].map((b, i) => <div key={i} className="bg-white rounded-xl p-3 flex items-center gap-3" data-id="element-6136">
                  <div className="text-[10px] font-mono font-bold text-brand-teal w-10" data-id="element-6137">
                    {b.time}
                  </div>
                  <div className="flex-1" data-id="element-6138">
                    <div className="text-xs font-bold text-brand-navy" data-id="element-6139">
                      {b.title}
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-6140">{b.host}</div>
                  </div>
                </div>)}
            </div>
            <div className="flex gap-2" data-id="element-6141">
              <button className="flex-1 py-2 bg-brand-navy text-white rounded-xl text-[10px] font-bold" data-id="element-6142">
                Materials
              </button>
              <button className="flex-1 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-[10px] font-bold" data-id="element-6143">
                Map
              </button>
            </div>
          </div>
        </div>
        <TutorialBubble b={{
        n: 1,
        text: '"Live now" card is computed from agenda_items. It auto-rotates as time passes — no manual updates needed.',
        pos: {
          top: '60px',
          left: '24px'
        },
        arrow: 'left',
        color: 'coral'
      }} data-id="element-6144" />
        <TutorialBubble b={{
        n: 2,
        text: 'Next 3 blocks filtered by attendee tier — VIPs see VIP-only items, parents see kids tracks, etc.',
        pos: {
          top: '180px',
          right: '24px'
        },
        arrow: 'right',
        color: 'amber'
      }} data-id="element-6145" />
      </div>
    </BrowserChrome>;
}
function P4Screen3() {
  return <BrowserChrome role="Attendee · Mobile" url="hub.acme.events/photos" data-id="element-6146">
      <div className="p-6 relative flex justify-center" data-id="element-6147">
        <div className="w-72 bg-white rounded-3xl border-4 border-slate-900 shadow-2xl overflow-hidden" data-id="element-6148">
          <div className="bg-slate-900 h-6 flex items-center justify-center" data-id="element-6149">
            <div className="w-12 h-1 bg-slate-700 rounded-full" data-id="element-6150" />
          </div>
          <div className="p-5" data-id="element-6151">
            <h4 className="text-base font-bold text-brand-navy mb-1" data-id="element-6152">
              Event photos & video
            </h4>
            <p className="text-[10px] text-slate-500 mb-3" data-id="element-6153">
              Share yours · everyone can see them
            </p>
            <div className="grid grid-cols-3 gap-1.5 mb-3" data-id="element-6154">
              {['bg-gradient-to-br from-amber-400 to-orange-500', 'bg-gradient-to-br from-purple-400 to-pink-500', 'bg-gradient-to-br from-brand-teal to-brand-navy', 'bg-gradient-to-br from-emerald-400 to-teal-500', 'bg-gradient-to-br from-rose-400 to-red-500', 'bg-gradient-to-br from-blue-400 to-indigo-500'].map((c, i) => <div key={i} className={`aspect-square rounded-lg ${c}`} data-id="element-6155" />)}
            </div>
            <button className="w-full py-3 bg-pink-500 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 mb-2" data-id="element-6156">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-6157" /> Upload photo or video
            </button>
            <div className="text-[10px] text-slate-500 text-center" data-id="element-6158">
              Videos go through Mux · ready in ~30s
            </div>
          </div>
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Photos write to materials with visibility=public. Videos route through Mux Direct Upload + encoding.',
        pos: {
          top: '80px',
          left: '24px'
        },
        arrow: 'left',
        color: 'pink'
      }} data-id="element-6159" />
        <TutorialBubble b={{
        n: 2,
        text: 'A post-event highlight reel is auto-generated from attendee uploads — delivered via the recap email.',
        pos: {
          bottom: '80px',
          right: '20px'
        },
        arrow: 'right',
        color: 'amber'
      }} data-id="element-6160" />
      </div>
    </BrowserChrome>;
}
function P4Screen4() {
  return <BrowserChrome role="Attendee · Mobile" url="hub.acme.events/feedback" data-id="element-6161">
      <div className="p-6 relative flex justify-center" data-id="element-6162">
        <div className="w-72 bg-white rounded-3xl border-4 border-slate-900 shadow-2xl overflow-hidden" data-id="element-6163">
          <div className="bg-slate-900 h-6 flex items-center justify-center" data-id="element-6164">
            <div className="w-12 h-1 bg-slate-700 rounded-full" data-id="element-6165" />
          </div>
          <div className="p-5" data-id="element-6166">
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-coral mb-1" data-id="element-6167">
              Thanks for joining us
            </div>
            <h4 className="text-base font-bold text-brand-navy mb-3" data-id="element-6168">
              How was your offsite?
            </h4>
            <div className="bg-slate-50 rounded-xl p-3 mb-3" data-id="element-6169">
              <div className="text-[11px] text-slate-600 mb-2" data-id="element-6170">
                Overall experience
              </div>
              <div className="flex gap-1" data-id="element-6171">
                {[1, 2, 3, 4, 5].map(s => <button key={s} className={`w-9 h-9 rounded-lg text-base ${s <= 4 ? 'bg-amber-400 text-white' : 'bg-white border border-slate-200 text-slate-300'}`} data-id="element-6172">
                    ★
                  </button>)}
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 mb-4" data-id="element-6173">
              <div className="text-[11px] text-slate-600 mb-2" data-id="element-6174">
                One commitment you're taking home
              </div>
              <div className="bg-white rounded-lg p-2 border border-slate-200 text-[11px] text-slate-700" data-id="element-6175">
                Ship the Q2 plan by Apr 15...
              </div>
            </div>
            <button className="w-full py-2.5 bg-brand-coral text-white rounded-xl font-bold text-xs mb-2" data-id="element-6176">
              Submit feedback
            </button>
            <button className="w-full py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5" data-id="element-6177">
              <CrownIcon className="w-3 h-3" data-id="element-6178" /> Leave a Google review
            </button>
          </div>
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Sent within 48h of Completed. Each answer writes to survey_responses with attendee_id + answers (jsonb).',
        pos: {
          top: '60px',
          left: '24px'
        },
        arrow: 'left',
        color: 'amber'
      }} data-id="element-6179" />
        <TutorialBubble b={{
        n: 2,
        text: 'Commitments are surfaced in the 30/60/90 follow-up cadence — a private nudge to the attendee.',
        pos: {
          top: '200px',
          right: '24px'
        },
        arrow: 'right',
        color: 'coral'
      }} data-id="element-6180" />
        <TutorialBubble b={{
        n: 3,
        text: 'Review prompt is opt-in. 4+ star → deep link to Google / TripAdvisor. <4 stars routes to private feedback.',
        pos: {
          bottom: '60px',
          right: '20px'
        },
        arrow: 'bottom',
        color: 'teal'
      }} data-id="element-6181" />
      </div>
    </BrowserChrome>;
}
/* ----- P5–P9 lightweight single-screens ----- */
function P5Screen() {
  return <BrowserChrome role="Hotel Admin" url="offsiteplus.app/admin/revenue" data-id="element-6182">
      <div className="p-6 relative" data-id="element-6183">
        <h3 className="text-lg font-bold text-brand-navy mb-4" data-id="element-6184">
          Property revenue · Q1 2026
        </h3>
        <div className="grid grid-cols-4 gap-3 mb-4" data-id="element-6185">
          {[{
          label: 'Total revenue',
          val: '$184k',
          color: 'bg-brand-coral text-white'
        }, {
          label: 'Events',
          val: '42',
          color: 'bg-white border border-slate-200 text-brand-navy'
        }, {
          label: 'Avg margin',
          val: '38%',
          color: 'bg-white border border-slate-200 text-brand-navy'
        }, {
          label: 'Rebook rate',
          val: '64%',
          color: 'bg-brand-teal text-white'
        }].map((k, i) => <Card key={i} className={`p-4 ${k.color}`} data-id="element-6186">
              <div className="text-[10px] uppercase font-bold opacity-70 mb-1" data-id="element-6187">
                {k.label}
              </div>
              <div className="text-2xl font-black" data-id="element-6188">{k.val}</div>
            </Card>)}
        </div>
        <Card className="p-5" data-id="element-6189">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3" data-id="element-6190">
            Revenue by event type
          </div>
          <div className="space-y-2" data-id="element-6191">
            {[{
            type: 'Corporate offsites',
            val: 78,
            money: '$92k'
          }, {
            type: 'Conferences',
            val: 52,
            money: '$48k'
          }, {
            type: 'Weddings',
            val: 41,
            money: '$32k'
          }, {
            type: 'Birthdays & socials',
            val: 8,
            money: '$4k'
          }].map((r, i) => <div key={i} className="flex items-center gap-3" data-id="element-6192">
                <div className="text-xs font-bold text-brand-navy w-44" data-id="element-6193">
                  {r.type}
                </div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-6194">
                  <div className="h-full bg-brand-coral rounded-full" style={{
                width: `${r.val}%`
              }} data-id="element-6195" />
                </div>
                <div className="text-xs font-bold text-slate-700 w-16 text-right" data-id="element-6196">
                  {r.money}
                </div>
              </div>)}
          </div>
        </Card>
        <TutorialBubble b={{
        n: 1,
        text: 'Admin sees revenue, attach-rate, margin, and rebook rate across every event the property hosts.',
        pos: {
          top: '20px',
          right: '24px'
        },
        arrow: 'right',
        color: 'navy'
      }} data-id="element-6197" />
        <TutorialBubble b={{
        n: 2,
        text: 'Comparison view: corporate is the money lane, socials are the goodwill lane — exactly as designed.',
        pos: {
          bottom: '40px',
          left: '24px'
        },
        arrow: 'left',
        color: 'coral'
      }} data-id="element-6198" />
      </div>
    </BrowserChrome>;
}
function P6Screen() {
  return <BrowserChrome role="Platform Admin" url="offsiteplus.app/platform/tenants" data-id="element-6199">
      <div className="p-6 relative" data-id="element-6200">
        <h3 className="text-lg font-bold text-brand-navy mb-4" data-id="element-6201">
          Hotel tenants
        </h3>
        <Card className="overflow-hidden" data-id="element-6202">
          {[{
          name: 'Lamplighter Sonoma',
          events: 42,
          revenue: '$184k',
          status: 'Active'
        }, {
          name: 'Vista Bay Resort',
          events: 28,
          revenue: '$96k',
          status: 'Active'
        }, {
          name: 'Cedar Lodge',
          events: 12,
          revenue: '$28k',
          status: 'Onboarding'
        }].map((t, i) => <div key={i} className="grid grid-cols-5 gap-3 px-4 py-3 items-center border-b border-slate-100 last:border-0" data-id="element-6203">
              <div className="col-span-2 text-sm font-bold text-brand-navy" data-id="element-6204">
                {t.name}
              </div>
              <div className="text-xs text-slate-500" data-id="element-6205">{t.events} events</div>
              <div className="text-xs font-bold text-brand-navy" data-id="element-6206">
                {t.revenue}
              </div>
              <div data-id="element-6207">
                <Pill color={t.status === 'Active' ? 'green' : 'amber'} data-id="element-6208">
                  {t.status}
                </Pill>
              </div>
            </div>)}
        </Card>
        <TutorialBubble b={{
        n: 1,
        text: 'Platform Admin manages all hotel tenants. Each row is one row in the hotels table.',
        pos: {
          top: '100px',
          right: '24px'
        },
        arrow: 'right',
        color: 'navy'
      }} data-id="element-6209" />
      </div>
    </BrowserChrome>;
}
function P7Screen() {
  return <BrowserChrome role="Facilitator" url="offsiteplus.app/facilitator/EVT-2641" data-id="element-6210">
      <div className="p-6 relative" data-id="element-6211">
        <h3 className="text-lg font-bold text-brand-navy mb-1" data-id="element-6212">
          Strategy Workshop · live
        </h3>
        <div className="text-xs text-slate-500 mb-4" data-id="element-6213">
          24 pax · Day 2 · 10:30 → 12:00
        </div>
        <Card className="p-4 mb-3 bg-brand-teal text-white" data-id="element-6214">
          <div className="text-[10px] uppercase font-bold opacity-70 mb-1" data-id="element-6215">
            Live poll · 18 of 24 responding
          </div>
          <div className="text-sm font-bold mb-2" data-id="element-6216">
            What's blocking faster decisions?
          </div>
          <div className="space-y-1.5 text-xs" data-id="element-6217">
            <div className="flex items-center gap-2" data-id="element-6218">
              <div className="flex-1 bg-white/20 rounded h-5 relative overflow-hidden" data-id="element-6219">
                <div className="absolute inset-y-0 left-0 bg-white/40 rounded" style={{
                width: '52%'
              }} data-id="element-6220" />
                <div className="absolute inset-0 flex items-center px-2 font-bold" data-id="element-6221">
                  Misaligned priorities
                </div>
              </div>
              <div className="text-xs font-bold w-8" data-id="element-6222">52%</div>
            </div>
            <div className="flex items-center gap-2" data-id="element-6223">
              <div className="flex-1 bg-white/20 rounded h-5 relative overflow-hidden" data-id="element-6224">
                <div className="absolute inset-y-0 left-0 bg-white/30 rounded" style={{
                width: '34%'
              }} data-id="element-6225" />
                <div className="absolute inset-0 flex items-center px-2 font-bold" data-id="element-6226">
                  Slow approval cycles
                </div>
              </div>
              <div className="text-xs font-bold w-8" data-id="element-6227">34%</div>
            </div>
          </div>
        </Card>
        <Card className="p-3" data-id="element-6228">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2" data-id="element-6229">
            Session notes (private to facilitator)
          </div>
          <div className="text-xs text-slate-700 leading-relaxed" data-id="element-6230">
            Strong energy on alignment thread. Pull this into Day 3
            commitments...
          </div>
        </Card>
        <TutorialBubble b={{
        n: 1,
        text: 'Live polls write to survey_responses in real time. Facilitator sees aggregate, attendees see their own answer.',
        pos: {
          top: '100px',
          right: '20px'
        },
        arrow: 'right',
        color: 'teal'
      }} data-id="element-6231" />
      </div>
    </BrowserChrome>;
}
function P8Screen() {
  return <BrowserChrome role="Sponsor" url="hub.acme.events/sponsor/momentum-tools" data-id="element-6232">
      <div className="p-6 relative" data-id="element-6233">
        <h3 className="text-lg font-bold text-brand-navy mb-4" data-id="element-6234">
          Your sponsor page
        </h3>
        <div className="grid grid-cols-2 gap-3" data-id="element-6235">
          <Card className="p-4" data-id="element-6236">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2" data-id="element-6237">
              Public page preview
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-white p-3 rounded-lg border border-slate-200" data-id="element-6238">
              <div className="w-10 h-10 bg-purple-500 rounded-lg mb-2" data-id="element-6239" />
              <div className="text-sm font-bold text-brand-navy" data-id="element-6240">
                Momentum Tools
              </div>
              <div className="text-[11px] text-slate-600 mb-2" data-id="element-6241">
                Premium sponsor · Day 1 lunch
              </div>
              <button className="text-[10px] font-bold text-purple-600" data-id="element-6242">
                Book a meeting →
              </button>
            </div>
          </Card>
          <Card className="p-4" data-id="element-6243">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2" data-id="element-6244">
              Leads captured
            </div>
            <div className="text-3xl font-black text-purple-600" data-id="element-6245">12</div>
            <div className="text-[10px] text-slate-500 mb-3" data-id="element-6246">
              meeting requests · 4 confirmed
            </div>
            <button className="text-[10px] font-bold text-purple-600" data-id="element-6247">
              Export CSV →
            </button>
          </Card>
        </div>
        <TutorialBubble b={{
        n: 1,
        text: 'Sponsors only see their own page + leads. RLS isolates them from the rest of the event.',
        pos: {
          top: '60px',
          right: '24px'
        },
        arrow: 'right',
        color: 'purple'
      }} data-id="element-6248" />
      </div>
    </BrowserChrome>;
}
function P9Screen() {
  return <BrowserChrome role="Read-only Viewer" url="hub.acme.events/view/parents" data-id="element-6249">
      <div className="p-6 relative" data-id="element-6250">
        <h3 className="text-lg font-bold text-brand-navy mb-4" data-id="element-6251">
          WNBA West Training · parents view
        </h3>
        <Card className="p-5 bg-amber-50/50 border-amber-200" data-id="element-6252">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-2" data-id="element-6253">
            Today · Apr 14
          </div>
          <div className="space-y-2" data-id="element-6254">
            <div className="bg-white p-3 rounded border border-slate-200" data-id="element-6255">
              <div className="text-xs font-bold text-brand-navy" data-id="element-6256">
                09:00 Practice · Court A
              </div>
              <div className="text-[10px] text-slate-500" data-id="element-6257">Coach K. Patel</div>
            </div>
            <div className="bg-white p-3 rounded border border-slate-200" data-id="element-6258">
              <div className="text-xs font-bold text-brand-navy" data-id="element-6259">
                12:30 Lunch · Pavilion
              </div>
            </div>
          </div>
        </Card>
        <TutorialBubble b={{
        n: 1,
        text: 'Read-only viewers (parents, board members, stakeholders) get scoped access via a granted link.',
        pos: {
          top: '100px',
          right: '24px'
        },
        arrow: 'right',
        color: 'amber'
      }} data-id="element-6260" />
      </div>
    </BrowserChrome>;
}
/* ===========================================================
   ROLE WORKFLOW DATA
=========================================================== */
type AISkill = {
  name: string;
  model: string;
  purpose: string;
  inputs: string[];
  outputs: string[];
  tone?: string;
};
type ScreenDef = {
  title: string;
  Component: React.FC;
  db: {
    tables: string[];
    ops: string[];
  };
  next: string;
  ai?: AISkill;
};
type RoleDef = {
  id: string;
  role: string;
  priority: number;
  tier: string;
  tierColor: string;
  scope: string;
  icon: React.FC<{
    className?: string;
  }>;
  iconColor: string;
  intro: string;
  screens: ScreenDef[];
};
export const ROLE_WORKFLOWS: RoleDef[] = [{
  id: 'p1',
  role: 'Hotel Sales Manager',
  priority: 1,
  tier: 'P1 · Build First',
  tierColor: 'bg-brand-coral text-white',
  scope: 'Property',
  icon: UploadCloudIcon,
  iconColor: 'bg-brand-coral text-white',
  intro: 'The very first workflow we ship. Sales Manager turns a stack of bookings into structured events, picks packages, and sends client-ready proposals — in under 15 minutes per event.',
  screens: [{
    title: 'Upload book of business',
    Component: P1Screen1,
    db: {
      tables: ['hotels', 'memberships', 'audit_log'],
      ops: ['Authenticate manager via memberships.role = "sales_manager"', 'Stream uploaded files to S3 + queue parser jobs', 'Log upload action to audit_log']
    },
    next: 'Parser pipeline runs asynchronously. Manager is redirected to the Review screen as soon as the first file finishes.'
  }, {
    title: 'Review parsed bookings',
    Component: P1Screen2,
    db: {
      tables: ['clients', 'events', 'event_venues', 'audit_log'],
      ops: ['INSERT each booking into clients + events', 'Set events.status = "Draft" + event_type from classifier', 'Confidence score stored on events.detection_meta (jsonb)']
    },
    ai: {
      name: 'Book of Business Parser + Event Classifier',
      model: 'claude-sonnet-4',
      purpose: 'Extracts structured booking data from messy PDFs and CSVs, then classifies each booking into an event type (Corporate Offsite, Wedding, Sports, Birthday, etc.) with a confidence score.',
      inputs: ['Raw PDF / CSV text from S3', 'Hotel-level event-type taxonomy', 'Historical labeled bookings (RAG)'],
      outputs: ['client_name, event_dates, pax, contract_value', 'event_type + confidence (0–1)', 'Reasoning trace stored in events.detection_meta'],
      tone: 'Confidence < 0.85 routes to manual review. Never hallucinate values — leave nulls when unsure.'
    },
    next: 'Manager clicks any event row → routes into the Package Picker screen to lock the tier.'
  }, {
    title: 'Pick the right package',
    Component: P1Screen3,
    db: {
      tables: ['packages', 'events', 'audit_log'],
      ops: ['Read recommended package from rules + LLM tiebreaker', 'Write events.package_id + freeze feature_flags', 'Log override + reason if manager declines recommendation']
    },
    ai: {
      name: 'Package Recommendation · Rules + LLM Tiebreaker',
      model: 'claude-haiku-4',
      purpose: 'Deterministic rules pick the package 90% of the time (event type + pax + budget). When two packages tie, Claude breaks the tie and explains the reasoning so the Sales Manager can defend the choice.',
      inputs: ['Event type, pax, contract value, hotel pricing rules', 'Available packages for this hotel', 'Past attach-rate data per package (RAG)'],
      outputs: ['Recommended package_id', 'Plain-English rationale shown in the UI', 'Alt suggestions ranked'],
      tone: 'Optimize for hotel margin × close probability. Always show why.'
    },
    next: 'Package locks the modules that will appear on the event hub. Manager continues into the Proposal Generator.'
  }, {
    title: 'AI proposal · ready to send',
    Component: P1Screen4,
    db: {
      tables: ['events', 'proposals', 'messages'],
      ops: ['INSERT proposals row · status = Draft · ai_generation_meta (jsonb)', 'Wait for Coordinator approval before allowing send', 'On send: INSERT messages row · channel = email · attach proposal_id']
    },
    ai: {
      name: 'AI Proposal Generator',
      model: 'claude-sonnet-4',
      purpose: "Generates a client-ready proposal — hero, agenda outline, pricing breakdown, social proof — using the hotel's brand voice and the locked package. Output is editable, never auto-sent.",
      inputs: ['Event record (client, dates, pax, package, value)', 'Hotel brand kit (voice, palette, signature dishes, photos)', 'Past won proposals for similar event types (RAG)'],
      outputs: ['Proposal title + executive summary', 'Day-by-day agenda outline', 'Pricing table + add-ons', 'Personalized CTA + sender signature'],
      tone: 'Warm, hotel-branded, never generic. Surfaces source citations in the editor so the manager can verify every claim.'
    },
    next: 'Client opens the shared link → view event tracked on proposals.viewed_at → pipeline dashboard updates instantly.'
  }]
}, {
  id: 'p2',
  role: 'Event Coordinator',
  priority: 2,
  tier: 'P2 · Next',
  tierColor: 'bg-brand-teal text-white',
  scope: 'Event',
  icon: ClipboardCheckIcon,
  iconColor: 'bg-brand-teal text-white',
  intro: 'Operational owner of one event. Takes the hand-off from Sales, builds the agenda, uploads materials (including Mux video), tracks RSVPs, and runs the event-day checklist.',
  screens: [{
    title: 'My events dashboard',
    Component: P2Screen1,
    db: {
      tables: ['events', 'memberships', 'attendees'],
      ops: ['SELECT events WHERE coordinator_id = current_user', 'Compute setup progress from agenda + materials + attendees counts', 'Order by starts_at ASC']
    },
    next: 'Coordinator clicks any event → routes into the Agenda Builder for that event.'
  }, {
    title: 'Agenda builder',
    Component: P2Screen2,
    db: {
      tables: ['agenda_items', 'venues', 'audit_log'],
      ops: ['CRUD agenda_items per event_id', 'Validate venue availability via event_venues', 'Assign host_user_id from facilitators']
    },
    ai: {
      name: 'Agenda Template Suggester',
      model: 'claude-haiku-4',
      purpose: 'Given the event type, length, and pax, suggests a starter agenda the Coordinator can drag-and-edit. Pulls from a curated library of past successful agendas for similar events.',
      inputs: ['Event type, duration, pax, package tier', 'Hotel venue inventory (rooms, capacities)', 'Library of past successful agendas (RAG)'],
      outputs: ['Day-by-day block plan with times', 'Recommended venue for each block', 'Buffer/break placement based on cohort fatigue rules'],
      tone: 'Always editable. Never inserts blocks without Coordinator confirmation.'
    },
    next: 'Saved agenda is visible to Client (for approval) and Attendees (after publish).'
  }, {
    title: 'Materials & vendors',
    Component: P2Screen3,
    db: {
      tables: ['materials', 'vendors', 'mux_assets'],
      ops: ['Upload non-video files to S3 → INSERT materials', 'Video: Mux Direct Upload → webhook updates materials.mux_asset_id', 'Vendor table links via event_vendors (many-to-many)']
    },
    ai: {
      name: 'Mux Video Auto-Transcribe + Summarize',
      model: 'claude-sonnet-4',
      purpose: "When a video uploads to Mux, the asset.ready webhook triggers Claude to transcribe (via Mux's native captions), then summarize into chapters, key quotes, and an attendee-friendly recap.",
      inputs: ['Mux asset ID + auto-generated transcript', 'Event context (agenda block, speaker)'],
      outputs: ['Chapter markers (auto-applied in Mux player)', 'TL;DR + key quotes', 'Searchable transcript stored on materials.transcript_jsonb'],
      tone: 'Faithful to source. Flags any unclear audio segments instead of guessing.'
    },
    next: 'All materials inherit event package visibility rules. Live streaming gated to Smart + Premium tiers.'
  }, {
    title: 'Event-day checklist',
    Component: P2Screen4,
    db: {
      tables: ['checklist_items', 'audit_log'],
      ops: ['Each check writes timestamp + actor_user_id to audit_log', 'Slip detection cron fires if a scheduled time passes without check', 'Coordinator gets push notification on slip']
    },
    next: 'When event reaches end_at, status transitions Live → Completed, triggering the Follow-up Engine.'
  }]
}, {
  id: 'p3',
  role: 'Client / Event Organizer',
  priority: 3,
  tier: 'P3 · Next',
  tierColor: 'bg-brand-teal text-white',
  scope: 'Event',
  icon: PaletteIcon,
  iconColor: 'bg-purple-600 text-white',
  intro: 'The external buyer (corporate planner, bride, parent, association). Customizes the hub, manages the guest list, approves AI-drafted content, and broadcasts updates.',
  screens: [{
    title: 'Hub customization',
    Component: P3Screen1,
    db: {
      tables: ['events', 'brand_kits'],
      ops: ['UPDATE events.theme_config (logo, accent, welcome message)', 'Brand kit cached at the CDN edge — instant attendee hub refresh', 'Versioned: every save creates a brand_kit_versions row']
    },
    next: 'Live preview updates every attendee’s hub. No deploy needed.'
  }, {
    title: 'Guest list',
    Component: P3Screen2,
    db: {
      tables: ['attendees', 'messages'],
      ops: ['INSERT into attendees with tier + rsvp_status', 'Trigger magic-link invite via messages (channel = email)', 'CSV import bulk-creates with header mapping']
    },
    next: 'Each attendee invite kicks off the pre-event sequence per package configuration.'
  }, {
    title: 'Approval queue',
    Component: P3Screen3,
    db: {
      tables: ['content_drafts', 'content_versions', 'audit_log'],
      ops: ['AI generations land in content_drafts (status = Draft)', 'Approve → INSERT into content_versions + flip Published flag', 'Rollback always available — versioning is immutable']
    },
    ai: {
      name: 'Hub Content Draft Generator',
      model: 'claude-sonnet-4',
      purpose: "Drafts every attendee-facing copy block — welcome message, arrival briefing, dress code, FAQ, daily pulse messages — in the client's approved voice. Nothing publishes without client approval.",
      inputs: ['Event details (theme, dates, location)', 'Client brand voice samples', 'Templates per content type (RAG)'],
      outputs: ['Drafts written to content_drafts (status = Draft)', 'Side-by-side diff view vs. previous version', 'Tone-match score so client can sanity-check'],
      tone: 'Never publishes. Every draft requires an explicit client click → versions are immutable so anything can be rolled back.'
    },
    next: 'Approved content publishes to attendees according to its scheduled send-time.'
  }, {
    title: 'Broadcast update',
    Component: P3Screen4,
    db: {
      tables: ['messages', 'attendees', 'opt_outs'],
      ops: ['For each attendee not in opt_outs: INSERT messages row', 'Personalize via attendee fields ({first_name}, {dietary})', 'Queue worker delivers · status flips Sent → Delivered → Opened']
    },
    ai: {
      name: 'Personalized Broadcast Writer',
      model: 'claude-haiku-4',
      purpose: 'Given a short prompt from the client ("Remind everyone about Friday\'s dress code"), Claude writes channel-appropriate copy (email vs. SMS vs. in-hub push), with per-attendee personalization tokens.',
      inputs: ['Client prompt + selected audience', 'Channel constraints (SMS character limit, email allowed)', 'Attendee profile fields available for merge'],
      outputs: ['Per-channel drafts (email, SMS, in-hub push)', 'Preview rendered for 3 sample attendees', 'Inline edit + approve before queue worker fires'],
      tone: 'Plain, human, never spammy. Always preview before send.'
    },
    next: 'Open rates roll into the post-event report. Every send is auditable.'
  }]
}, {
  id: 'p4',
  role: 'Attendee / Guest',
  priority: 4,
  tier: 'P4 · Next',
  tierColor: 'bg-amber-500 text-white',
  scope: 'Event',
  icon: QrCodeIcon,
  iconColor: 'bg-amber-500 text-white',
  intro: 'End user of the event hub. Mobile-first. Sees what is happening now, what is next, accesses materials, uploads media, and completes post-event feedback.',
  screens: [{
    title: 'Magic-link landing',
    Component: P4Screen1,
    db: {
      tables: ['attendees', 'auth_tokens', 'audit_log'],
      ops: ['Validate signed token → resolve attendee_id', 'Set last_seen_at on attendees', 'Log entry in audit_log (check-in)']
    },
    next: 'Attendee lands on the personalized hub home with QR + schedule one tap away.'
  }, {
    title: 'Now / Next + schedule',
    Component: P4Screen2,
    db: {
      tables: ['agenda_items', 'materials', 'attendees'],
      ops: ['SELECT agenda_items WHERE event_id + now() between start/end', 'Filter materials by visibility ∈ {public, attendee_tier}', 'Schedule view computed live, cached for 30s']
    },
    next: 'Each block links to its materials, location, and host bio.'
  }, {
    title: 'Photos & video',
    Component: P4Screen3,
    db: {
      tables: ['materials', 'mux_assets'],
      ops: ['Photos → S3 → INSERT materials with kind=image', 'Videos → Mux Direct Upload → mux_asset_id stored', 'Hub auto-refreshes when asset.ready webhook fires']
    },
    next: 'After the event, an auto-generated highlight reel stitches uploads together for the recap.'
  }, {
    title: 'Feedback + review',
    Component: P4Screen4,
    db: {
      tables: ['survey_responses', 'commitments', 'review_clicks'],
      ops: ['Star rating + answers → survey_responses (jsonb)', '"One commitment" stored separately for follow-up cadence', '4+ stars → review_clicks tracks Google / TripAdvisor click']
    },
    ai: {
      name: 'Sentiment + Commitment Classifier',
      model: 'claude-haiku-4',
      purpose: 'Reads each attendee\'s free-text answer and their "one commitment". Tags sentiment, extracts themes, and structures the commitment so the 30/60/90 follow-up cadence can nudge on it.',
      inputs: ['Free-text survey answers', 'Star rating + structured fields', 'Event context (theme, agenda blocks attended)'],
      outputs: ['Sentiment label + score', 'Top themes rolled up across cohort', 'Structured commitment (verb + object + due date)'],
      tone: 'Never quote an attendee by name to the client without anonymizing.'
    },
    next: '30/60/90-day cadence kicks in — private nudges on commitments + rebook offers for the client.'
  }]
}, {
  id: 'p5',
  role: 'Hotel Admin / Owner',
  priority: 5,
  tier: 'P5 · Phase 2',
  tierColor: 'bg-slate-600 text-white',
  scope: 'Property',
  icon: BarChart3Icon,
  iconColor: 'bg-blue-600 text-white',
  intro: 'Owner-level visibility. Revenue, attach-rate, margin, and rebooking across every event. Configures pricing rules and staff access.',
  screens: [{
    title: 'Revenue dashboard',
    Component: P5Screen,
    db: {
      tables: ['events', 'invoices', 'packages'],
      ops: ['Aggregate invoices by event_type and package_tier', 'Compute attach-rate, margin, rebook rate per cohort', 'Materialized view refreshes hourly']
    },
    ai: {
      name: 'Revenue Insight Surfacer',
      model: 'claude-sonnet-4',
      purpose: 'Watches the materialized revenue view. When a metric drifts (attach-rate drop, margin compression, slow rebook segment), Claude writes a plain-English explanation and a recommended next action.',
      inputs: ['Hourly materialized revenue snapshot', 'Trailing 90-day baseline', 'Hotel pricing rules + event mix'],
      outputs: ['Ranked list of insight cards', 'Suggested pricing-rule change (one click to apply)', 'Linked drill-down filters per insight'],
      tone: 'Quantified, no fluff. Always cites the numbers behind the insight.'
    },
    next: 'Admin spots margin opportunities → pushes a pricing rule update → next quote uses the new floor.'
  }]
}, {
  id: 'p6',
  role: 'Platform Admin',
  priority: 6,
  tier: 'P6 · Phase 2',
  tierColor: 'bg-slate-600 text-white',
  scope: 'Offsite+',
  icon: ShieldCheckIcon,
  iconColor: 'bg-brand-navy text-white',
  intro: 'Internal Offsite+ team. Onboards hotel tenants, manages global content templates, monitors platform health, and handles billing exceptions.',
  screens: [{
    title: 'Tenant management',
    Component: P6Screen,
    db: {
      tables: ['hotels', 'memberships', 'billing_accounts'],
      ops: ['CRUD hotels + staff onboarding flows', 'Move tenants through onboarding → active → suspended', 'Aggregate cross-tenant metrics for ops']
    },
    next: 'New tenants get a seeded environment + a Sales Manager invitation to start Workflow #1 immediately.'
  }]
}, {
  id: 'p7',
  role: 'Facilitator / Host',
  priority: 7,
  tier: 'P7 · Later',
  tierColor: 'bg-slate-200 text-slate-600',
  scope: 'Event',
  icon: Mic2Icon,
  iconColor: 'bg-emerald-500 text-white',
  intro: 'Workshop facilitators, MCs, coaches, and speakers. Runs live polls, captures session notes, sees attendee feedback in real time.',
  screens: [{
    title: 'Live session console',
    Component: P7Screen,
    db: {
      tables: ['polls', 'survey_responses', 'session_notes'],
      ops: ['Live poll responses stream via Supabase realtime', 'Aggregate hidden from attendees until poll closes', 'Private session_notes only visible to facilitator + coordinator']
    },
    ai: {
      name: 'Live Session Notes Synthesizer',
      model: 'claude-haiku-4',
      purpose: 'As the facilitator types or dictates notes during the session, Claude summarizes into themes, surfaces unresolved questions, and proposes agenda tweaks for the next block.',
      inputs: ['Streaming session_notes', 'Live poll aggregates', 'Original agenda + objectives'],
      outputs: ['Theme summary updated every 60s', 'Open questions list', 'Suggested next-block adjustment'],
      tone: 'Private to facilitator + coordinator. Never visible to attendees.'
    },
    next: 'Poll results and session notes feed the post-event report and the next-day agenda planning.'
  }]
}, {
  id: 'p8',
  role: 'Sponsor / Vendor',
  priority: 8,
  tier: 'P8 · Later',
  tierColor: 'bg-slate-200 text-slate-600',
  scope: 'Event',
  icon: HandshakeIcon,
  iconColor: 'bg-purple-500 text-white',
  intro: 'Conference sponsors, wedding vendors, donors. Limited access to edit their own page and capture leads.',
  screens: [{
    title: 'Sponsor page + leads',
    Component: P8Screen,
    db: {
      tables: ['sponsors', 'leads'],
      ops: ['RLS isolates sponsors to their own row only', 'Leads write via attendee meeting-request form', 'CSV export tracked for compliance']
    },
    next: 'Lead handoff to sponsor CRM → post-event report shows engagement value the sponsor delivered.'
  }]
}, {
  id: 'p9',
  role: 'Read-Only Viewer',
  priority: 9,
  tier: 'P9 · Later',
  tierColor: 'bg-slate-200 text-slate-600',
  scope: 'Event or Property',
  icon: EyeIcon,
  iconColor: 'bg-slate-500 text-white',
  intro: 'Stakeholders like parents, executives, and board members. Scoped view-only access via a granted link.',
  screens: [{
    title: 'Scoped view-only hub',
    Component: P9Screen,
    db: {
      tables: ['view_grants', 'audit_log'],
      ops: ['Signed view link maps to a row in view_grants', 'All reads are READ-ONLY enforced at RLS layer', 'Every grant + access logged for compliance']
    },
    next: 'View grants expire on schedule or when the event archives — no manual cleanup.'
  }]
}];
/* -----------------------------------------------------------
   Modal Component
----------------------------------------------------------- */
export function RoleWorkflowModal({
  roleId,
  onClose
}: {
  roleId: string | null;
  onClose: () => void;
}) {
  const role = ROLE_WORKFLOWS.find(r => r.id === roleId) || null;
  const [step, setStep] = useState(0);
  const [showTutorials, setShowTutorials] = useState(true);
  useEffect(() => {
    setStep(0);
    setShowTutorials(true);
  }, [roleId]);
  useEffect(() => {
    if (roleId) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [roleId]);
  return <AnimatePresence data-id="element-6261">
      {role && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-6262">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6263">
            {/* Header */}
            <div className="bg-gradient-to-br from-brand-navy to-brand-navy/90 text-white px-5 md:px-7 py-4 md:py-5 relative overflow-hidden shrink-0" data-id="element-6264">
              <div className="absolute top-0 right-0 w-96 h-96 bg-brand-teal/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3 pointer-events-none" data-id="element-6265" />
              <div className="relative flex items-start justify-between gap-4" data-id="element-6266">
                <div className="flex items-start gap-3 min-w-0" data-id="element-6267">
                  <div className={`w-11 h-11 rounded-xl ${role.iconColor} flex items-center justify-center shrink-0`} data-id="element-6268">
                    <role.icon className="w-5 h-5" data-id="element-6269" />
                  </div>
                  <div className="min-w-0" data-id="element-6270">
                    <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-6271">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${role.tierColor}`} data-id="element-6272">
                        {role.tier}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300" data-id="element-6273">
                        Scope · {role.scope}
                      </span>
                    </div>
                    <h2 className="text-lg md:text-xl font-serif-display font-bold mb-1 leading-tight" data-id="element-6274">
                      {role.role} · Workflow
                    </h2>
                    <p className="text-xs text-slate-300 max-w-2xl leading-relaxed line-clamp-2" data-id="element-6275">
                      {role.intro}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0" data-id="element-6276">
                  <button onClick={() => setShowTutorials(v => !v)} title={showTutorials ? 'Hide tutorial bubbles' : 'Show tutorial bubbles'} className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-bold transition-colors ${showTutorials ? 'bg-brand-coral text-white hover:bg-brand-coral/90' : 'bg-white/10 text-slate-300 hover:bg-white/20'}`} data-id="element-6277">
                    {showTutorials ? <>
                        <EyeOffIcon className="w-3.5 h-3.5" data-id="element-6278" />
                        Hide tutorial
                      </> : <>
                        <EyeIcon className="w-3.5 h-3.5" data-id="element-6279" />
                        Show tutorial
                      </>}
                  </button>
                  <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-id="element-6280">
                    <XIcon className="w-4 h-4" data-id="element-6281" />
                  </button>
                </div>
              </div>

              {/* Step pills */}
              <div className="relative flex gap-1.5 mt-3 overflow-x-auto pb-1" data-id="element-6282">
                {role.screens.map((s, i) => <button key={i} onClick={() => setStep(i)} className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-all ${i === step ? 'bg-brand-coral text-white shadow-lg' : 'bg-white/10 text-slate-300 hover:bg-white/20'}`} data-id="element-6283">
                    <span className="text-[10px] opacity-70 mr-1.5" data-id="element-6284">
                      {i + 1}
                    </span>
                    {s.title}
                  </button>)}
              </div>
            </div>

            {/* Body */}
            <div className="p-6 md:p-8 bg-slate-50 flex-1 overflow-y-auto min-h-0" data-id="element-6285">
              <TutorialContext.Provider value={showTutorials} data-id="element-6286">
                <AnimatePresence mode="wait" data-id="element-6287">
                  <motion.div key={`${role.id}-${step}`} initial={{
                opacity: 0,
                x: 12
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -12
              }} transition={{
                duration: 0.25
              }} data-id="element-6288">
                    {role.screens[step] && <>
                        {createElement(role.screens[step].Component)}
                        <FooterPanel db={role.screens[step].db} next={role.screens[step].next} ai={role.screens[step].ai} data-id="element-6289" />
                      </>}
                  </motion.div>
                </AnimatePresence>
              </TutorialContext.Provider>
            </div>

            {/* Footer navigation */}
            <div className="px-6 md:px-8 py-4 bg-white border-t border-slate-200 flex items-center justify-between shrink-0" data-id="element-6290">
              <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:text-brand-navy disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-6291">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-6292" />
                Previous
              </button>
              <div className="text-xs text-slate-500" data-id="element-6293">
                Screen {step + 1} of {role.screens.length}
              </div>
              {step === role.screens.length - 1 ? <button onClick={onClose} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-brand-navy text-white hover:bg-brand-navy/90" data-id="element-6294">
                  Close walkthrough
                  <CheckCircle2Icon className="w-4 h-4" data-id="element-6295" />
                </button> : <button onClick={() => setStep(s => Math.min(role.screens.length - 1, s + 1))} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-brand-coral text-white hover:bg-brand-coral/90" data-id="element-6296">
                  Next screen
                  <ArrowRightIcon className="w-4 h-4" data-id="element-6297" />
                </button>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}